import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Trash2, Heart, Plus, Minus, Check, CreditCard } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { promotionsAPI, paymentOptionsAPI } from '../services/api';
import ProgressBar from '../components/Checkout/ProgressBar';
import PaymentOptions from '../components/Payment/PaymentOptions';
import { useNotificationHook } from '../hooks/useNotificationHook';
import { getPaymentTypeWithInstallment } from '../utils/installmentUtils';
import { mapPaymentMethodToDisplay } from '../utils/paymentUtils';

const Checkout = () => {
  const { notify } = useNotificationHook();
  const { 
    items, 
    removeFromCart, 
    updateQuantity, 
    addToWishlist, 
    removeFromWishlist, 
    getSubtotal, 
    getDiscount,
    getTotal,
    applyCoupon,
    removeCoupon,
    appliedCoupon,
    isInWishlist 
  } = useCart();
  const { user, isAuthenticated } = useAuth();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    email: isAuthenticated && user ? user.email : '',
    firstName: isAuthenticated && user ? user.name?.split(' ')[0] || '' : '',
    lastName: isAuthenticated && user ? user.name?.split(' ').slice(1).join(' ') || '' : '',
    password: '',
    confirmPassword: '',
    address: isAuthenticated && user ? user.address || '' : '',
    createAccount: !isAuthenticated, // Only show for guests
    agreeToTermsAndPrivacy: false
  });

  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  
  // Payment Options State
  const [selectedPaymentOption, setSelectedPaymentOption] = useState('full');
  const [paymentOptionsCoupon, setPaymentOptionsCoupon] = useState(null);
  const [paymentOptionsConfig, setPaymentOptionsConfig] = useState(null);
  const [emiDetails, setEmiDetails] = useState(null);
  const [showTermsError, setShowTermsError] = useState(false);

  // Ref for the terms checkbox
  const termsCheckboxRef = useRef(null);

  // Update form data when authentication state changes
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        email: user.email || '',
        firstName: user.name?.split(' ')[0] || '',
        lastName: user.name?.split(' ').slice(1).join(' ') || '',
        address: user.address || '',
        createAccount: false // Hide account creation for logged-in users
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        email: '',
        firstName: '',
        lastName: '',
        address: '',
        createAccount: true // Show account creation for guests
      }));
    }
  }, [isAuthenticated, user]);

  // Fetch available coupon codes from promotions
  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        setIsLoadingCoupons(true);
        const response = await promotionsAPI.getAll({
          type: 'coupon'
        });
        
        if (response?.data?.success) {
          const validCoupons = response.data.data.promotions?.filter(coupon => {
            // Check if coupon is active
            if (!coupon.is_active) return false;
            
            // Check validity dates
            const now = new Date();
            if (coupon.valid_from && new Date(coupon.valid_from) > now) return false;
            if (coupon.valid_until && new Date(coupon.valid_until) < now) return false;
            
            // Check if coupon code exists
            if (!coupon.coupon_code) return false;
            
            return true;
          }) || [];
          
          setAvailableCoupons(validCoupons);
          console.log('Available coupons:', validCoupons);
        }
      } catch (err) {
        console.error('Error fetching coupons:', err);
      } finally {
        setIsLoadingCoupons(false);
      }
    };

    fetchCoupons();
  }, []);

  // Auto-apply original coupon for split payments
  useEffect(() => {
    // Check if there are split payments in the cart
    const splitPaymentItems = items.filter(item => item.type === 'split_payment');
    
    if (splitPaymentItems.length > 0 && !appliedCoupon) {
      // Get the original coupon from the first split payment item
      const firstSplitPayment = splitPaymentItems[0];
      const originalCoupon = firstSplitPayment.paymentData?.original_coupon;
      
      if (originalCoupon) {
        // Apply the original coupon automatically
        applyCoupon(originalCoupon);
        notify.success(`Original coupon "${originalCoupon.coupon_code}" automatically applied!`, 'Coupon Applied');
      }
    }
  }, [items, appliedCoupon, applyCoupon, notify]);

  // Handle coupon code from UserCoupons component
  useEffect(() => {
    if (location.state?.couponCode) {
      setCouponCode(location.state.couponCode);
      if (location.state.message) {
        notify.success(location.state.message, 'Coupon Ready');
      }
      // Clear the location state to prevent showing the message again
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname, notify]);


  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear specific error when user interacts with the field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
      
      // Show positive feedback when user fixes an error
      if (name === 'agreeToTermsAndPrivacy' && checked) {
        setShowTermsError(false);
        notify.success('Terms and conditions accepted!', 'Validation Passed');
      }
    }
  };

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity >= 1) {
      updateQuantity(productId, newQuantity);
    }
  };

  const handleRemoveFromCart = (productId) => {
    removeFromCart(productId);
  };

  const handleWishlistToggle = (product) => {
    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product);
    }
  };

  const handleApplyCoupon = () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      notify.warning('Please enter a coupon code to apply.', 'Coupon Code Required');
      return;
    }

    // Find coupon in available coupons
    const coupon = availableCoupons.find(c => 
      c.coupon_code.toUpperCase() === couponCode.toUpperCase()
    );

    if (coupon) {
      // Check if coupon is already applied
      if (appliedCoupon && appliedCoupon.code === coupon.coupon_code) {
        setCouponError('Coupon already applied');
        notify.warning('This coupon is already applied to your order.', 'Duplicate Coupon');
        return;
      }

      // Apply the coupon
      applyCoupon({
        code: coupon.coupon_code,
        discount_percentage: coupon.discount_percentage || 0,
        discount_amount: coupon.discount_amount || 0,
        title: coupon.title,
        description: coupon.description
      });
      
      setCouponError('');
      setCouponCode('');
      
      // Show success notification
      const discountText = coupon.discount_amount && coupon.discount_amount > 0 
        ? `₹${coupon.discount_amount} off`
        : coupon.discount_percentage && coupon.discount_percentage > 0
        ? `${coupon.discount_percentage}% off`
        : 'discount applied';
      
      notify.success(`Coupon "${coupon.coupon_code}" applied successfully! (${discountText})`, 'Coupon Applied');
    } else {
      setCouponError('Invalid coupon code');
      notify.error('Invalid coupon code. Please check and try again.', 'Invalid Coupon');
    }
  };

  const handleRemoveCoupon = () => {
    removeCoupon();
    setCouponError('');
    
    // Show notification
    notify.info('Coupon removed successfully', 'Coupon Removed');
  };



  // Payment Options Handlers
  const handlePaymentOptionChange = (option) => {
    setSelectedPaymentOption(option);
  };

  const handlePaymentOptionsConfigLoaded = (config) => {
    setPaymentOptionsConfig(config);
  };

  const handleEmiDetailsChange = useCallback((details) => {
    setEmiDetails(details);
  }, []);

  const handlePaymentOptionsCouponChange = (coupon) => {
    setPaymentOptionsCoupon(coupon);
    // Also update the cart context if coupon is applied
    if (coupon) {
      applyCoupon(coupon);
    } else {
      removeCoupon();
    }
  };

  const handleBackToCart = () => {
    navigate('/cart');
  };

  const handleProceedToPayment = () => {
    // Clear previous errors
    setFormErrors({});
    
    // Validate form
    const errors = {};
    
    if (!formData.email || !formData.firstName || !formData.lastName || !formData.address) {
      errors.general = 'Please fill in all required fields';
    }

    if (!formData.agreeToTermsAndPrivacy) {
      errors.terms = 'Please agree to the terms and conditions and privacy policy';
    }

    if (formData.createAccount) {
      if (!formData.password || !formData.confirmPassword) {
        errors.password = 'Please enter password and confirm password';
      } else if (formData.password !== formData.confirmPassword) {
        errors.password = 'Passwords do not match';
      }
    }
    
    // If there are errors, set them, show notification, and return
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      
      // If terms validation failed, scroll to the checkbox
      if (errors.terms) {
        setShowTermsError(true);
        // Scroll to the checkbox using ref
        if (termsCheckboxRef.current) {
          termsCheckboxRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
          // Add a brief focus to highlight the checkbox
          setTimeout(() => {
            termsCheckboxRef.current.focus();
          }, 500);
        }
        // Clear the error animation after 3 seconds
        setTimeout(() => {
          setShowTermsError(false);
        }, 3000);
      }
      
      // Show notification for the first error
      const firstError = Object.values(errors)[0];
      if (firstError) {
        notify.warning(firstError, 'Validation Error');
      }
      
      return;
    }

    // Save checkout data to localStorage for payment page
    const checkoutData = {
      ...formData,
      selectedPaymentOption,
      paymentOptionsCoupon,
      // EMI details if applicable
      ...(emiDetails && selectedPaymentOption === 'emi' ? emiDetails : {})
    };
    

    localStorage.setItem('checkoutData', JSON.stringify(checkoutData));
    
    // Show success notification
    notify.success('Checkout information saved successfully!', 'Proceeding to Payment');
    
    navigate('/payment');
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
        <ProgressBar currentStep="checkout" />
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
          <div className="text-center">
            <div className="w-32 h-32 mx-auto mb-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
              <ArrowLeft className="w-16 h-16 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Your cart is empty
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-12">
              Please add some products to your cart before proceeding to checkout.
            </p>
            <button
              onClick={() => navigate('/products')}
              className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-4 rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow"
            >
              Browse Products
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
      <ProgressBar currentStep="checkout" />
      
      <div className="py-12">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2">
                <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20 p-8">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                    Checkout Information
                  </h2>

                  {/* General Form Errors */}
                  {formErrors.general && (
                    <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <span className="text-red-600">⚠</span>
                        <span className="text-red-800 dark:text-red-200 text-sm">{formErrors.general}</span>
                      </div>
                    </div>
                  )}

                  {/* Account Options - Only show for guest users */}
                  {!isAuthenticated && (
                    <div className="mb-6">
                      <label className="flex items-center space-x-2 mb-4">
                        <input
                          type="checkbox"
                          name="createAccount"
                          checked={formData.createAccount}
                          onChange={handleInputChange}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-gray-900 dark:text-white">Create an account for faster checkout</span>
                      </label>
                    </div>
                  )}

                  {/* User Info Display for Logged-in Users */}
                  {isAuthenticated && user && (
                    <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                        <span className="text-green-800 dark:text-green-400 font-medium">Logged in as {user.name}</span>
                      </div>
                      <p className="text-green-700 dark:text-green-300 text-sm">
                        Your account information will be used for this order.
                      </p>
                    </div>
                  )}

                  {/* Customer Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        readOnly={isAuthenticated}
                        className={`w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                          isAuthenticated ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''
                        }`}
                        required
                      />
                      {isAuthenticated && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Email is pre-filled from your account
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        required
                      />
                    </div>
                    {formData.createAccount && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Password *
                          </label>
                          <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                              formErrors.password ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                            }`}
                            required={formData.createAccount}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Confirm Password *
                          </label>
                          <input
                            type="password"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                              formErrors.password ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                            }`}
                            required={formData.createAccount}
                          />
                        </div>
                        {formErrors.password && (
                          <div className="col-span-2">
                            <p className="text-red-600 text-sm mt-2 flex items-center">
                              <span className="mr-1">⚠</span>
                              {formErrors.password}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Address */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Address *
                    </label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Enter your complete address"
                      required
                    />
                  </div>



                  {/* Profile Save Message */}
                  {isAuthenticated && user && (
                    <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Check className="w-5 h-5 text-blue-600" />
                        <span className="text-blue-800 dark:text-blue-200 text-sm">
                          These details are being added to your profile. You can change them later if needed.
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Terms and Privacy */}
                  <div className={`mb-6 transition-all duration-300 ${showTermsError ? 'animate-pulse' : ''}`}>
                    <label className="flex items-start space-x-2">
                      <input
                        ref={termsCheckboxRef}
                        type="checkbox"
                        name="agreeToTermsAndPrivacy"
                        checked={formData.agreeToTermsAndPrivacy}
                        onChange={handleInputChange}
                        className={`mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500 transition-all duration-300 ${
                          formErrors.terms ? 'border-red-500 ring-2 ring-red-500 ring-opacity-50' : ''
                        }`}
                        required
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        I agree to the <a href="/terms-conditions" className="text-primary-600 hover:text-primary-700">Terms and Conditions</a> and <a href="/privacy-policy" className="text-primary-600 hover:text-primary-700">Privacy Policy</a>
                      </span>
                    </label>
                    {formErrors.terms && (
                      <p className={`text-red-600 text-sm mt-2 flex items-center bg-red-50 dark:bg-red-900/20 p-2 rounded-lg border border-red-200 dark:border-red-800 transition-all duration-300 ${
                        showTermsError ? 'animate-pulse' : ''
                      }`}>
                        <span className={`mr-1 transition-all duration-300 ${
                          showTermsError ? 'animate-bounce' : ''
                        }`}>⚠</span>
                        {formErrors.terms}
                      </p>
                    )}
                  </div>

                  {/* Coupon Code */}
                  <div className="mb-8">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      Coupon Code
                    </label>
                    <div className="flex flex-col sm:flex-row sm:space-x-3 gap-3">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        className="w-full sm:flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white shadow-lg transition-all duration-300"
                        placeholder="Enter coupon code"
                      />
                      <button
                        onClick={handleApplyCoupon}
                        className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:scale-105 text-white font-bold rounded-2xl transition-all duration-300 shadow-glow"
                      >
                        Apply
                      </button>
                    </div>
                    {couponError && (
                      <p className="text-red-600 text-sm mt-3 flex items-center bg-red-50 dark:bg-red-900/20 p-3 rounded-2xl border border-red-200 dark:border-red-800">
                        <span className="mr-2">⚠</span>
                        {couponError}
                      </p>
                    )}
                    {appliedCoupon && (
                      <div className="flex items-center justify-between mt-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-2xl border border-green-200 dark:border-green-800">
                        <span className="text-green-700 dark:text-green-400 text-sm font-semibold">
                          Coupon applied: {appliedCoupon.code} 
                          {appliedCoupon.discount_amount && appliedCoupon.discount_amount > 0 ? (
                            <span> (₹{appliedCoupon.discount_amount} off)</span>
                          ) : appliedCoupon.discount_percentage && appliedCoupon.discount_percentage > 0 ? (
                            <span> ({appliedCoupon.discount_percentage}% off)</span>
                          ) : (
                            <span> (Discount applied)</span>
                          )}
                        </span>
                        <button
                          onClick={handleRemoveCoupon}
                          className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 font-semibold hover:scale-105 transition-all duration-300"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Payment Options - Hide when cart contains payment items or split payment items */}
                  {!items.some(item => item.type === 'payment' || item.type === 'split_payment') && (
                    <div className="mb-6">
                      <PaymentOptions
                        onPaymentOptionChange={handlePaymentOptionChange}
                        selectedPaymentOption={selectedPaymentOption}
                        appliedCoupon={paymentOptionsCoupon}
                        onCouponChange={handlePaymentOptionsCouponChange}
                        totalAmount={getTotal()}
                        formatPrice={formatPrice}
                        onConfigLoaded={handlePaymentOptionsConfigLoaded}
                        onEmiDetailsChange={handleEmiDetailsChange}
                        productId={(items.find(i => !i.type) || {}).id}
                        disableFullPaymentCoupon={Boolean((items.find(i => !i.type) || {}).disable_full_payment_coupon)}
                      />
                    </div>
                  )}
                  
                  {/* Payment Information for Payment Items and Split Payment Items */}
                  {items.some(item => item.type === 'payment' || item.type === 'split_payment') && (
                    <div className="mb-6">
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                        <div className="flex items-center space-x-3 mb-3">
                          <CreditCard className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                            Payment Details
                          </h3>
                        </div>
                        <div className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
                          <p>• You are making a payment for an existing EMI or Split Payment</p>
                          <p>• Payment options are not available for installment or split payments</p>
                          <p>• Proceed directly to payment processing</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Payment Methods Preview */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Available Payment Methods
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {['Cash On Delivery', 'Razorpay', 'Instamojo', 'Cashfree', 'Phonepe', 'Paytm', 'Gpay', 'Card Payment'].map((method) => (
                        <div key={method} className="flex items-center space-x-2 p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
                          <Check className="w-4 h-4 text-green-500" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{method}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20 p-8 sticky top-24">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                    Order Summary
                  </h3>

                  {/* Cart Items */}
                  <div className="space-y-4 mb-6">
                    {items.map((item) => {
                      // Use calculated price if available, otherwise fall back to original calculation
                      const price = item.calculatedPrice || parseFloat(item.regular_license_price || 0);
                      const total = price * item.quantity;
                      
                      return (
                        <div key={item.id} className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
                          <div className="w-12 h-12 bg-gray-200 dark:bg-gray-600 rounded-lg overflow-hidden">
                            {item.type === 'payment' || item.type === 'split_payment' ? (
                              <div className="w-full h-full flex items-center justify-center text-blue-600 dark:text-blue-400">
                                <CreditCard className="w-6 h-6" />
                              </div>
                            ) : item.image ? (
                              <img
                                src={item.image}
                                alt={item.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <span className="text-xs">No Image</span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                              {item.name}
                            </h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {item.category_name || item.category}
                            </p>
                            
                            {/* Display payment-specific information */}
                            {(item.type === 'payment' || item.type === 'split_payment') && item.paymentData && (
                              <div className="mt-1 space-y-1">
                                <p className="text-xs text-blue-600 dark:text-blue-400">
                                  Type: {item.type === 'split_payment' ? 
                                    `Split Payment - ${item.paymentData.split_payment_text || 'Remaining Amount'}` : 
                                    getPaymentTypeWithInstallment(item.paymentData.type, item.paymentData.installment_number)
                                  }
                                </p>
                                {item.paymentData.next_payment_date && (
                                  <p className="text-xs text-green-600 dark:text-green-400">
                                    Due Date: {new Date(item.paymentData.next_payment_date).toLocaleDateString()}
                                  </p>
                                )}
                                {item.paymentData.payment_method && !item.paymentData.payment_method.toLowerCase().includes('cash on delivery') && (
                                  <p className="text-xs text-purple-600 dark:text-purple-400">
                                    Method: {mapPaymentMethodToDisplay(item.paymentData.payment_method)}
                                  </p>
                                )}
                              </div>
                            )}
                            
                            {/* Display selected options for products */}
                            {!item.type && item.selectedLicense && (
                              <div className="mt-1 space-y-1">
                                <p className="text-xs text-primary-600 dark:text-primary-400">
                                  License: {item.selectedLicense === 'regular' ? 'Regular' : 'Extended'}
                                </p>
                                {item.selectedAddons && item.selectedAddons.length > 0 && (
                                  <p className="text-xs text-blue-600 dark:text-blue-400">
                                    Add-ons: {item.selectedAddons.length} selected
                                  </p>
                                )}
                                {item.extendedSupport && (
                                  <p className="text-xs text-green-600 dark:text-green-400">
                                    Extended Support: Yes
                                  </p>
                                )}
                                {appliedCoupon && appliedCoupon.type === 'full_payment' && item.disable_full_payment_coupon && (
                                  <p className="text-xs text-red-600 dark:text-red-400">
                                    Coupon discount is not applicable for this product.
                                  </p>
                                )}
                              </div>
                            )}
                            
                            {item.type !== 'payment' && item.type !== 'split_payment' && (
                              <div className="flex items-center space-x-2 mt-2">
                                <button
                                  onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                                  className="w-6 h-6 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                >
                                  <Minus className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                                </button>
                                <span className="text-xs text-gray-900 dark:text-white font-medium">
                                  {item.quantity}
                                </span>
                                <button
                                  onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                                  className="w-6 h-6 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                >
                                  <Plus className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold text-gray-900 dark:text-white">
                              {formatPrice(total)}
                            </div>
                            <div className="flex items-center space-x-1 mt-2">
                              {item.type !== 'payment' && item.type !== 'split_payment' && (
                                <button
                                  onClick={() => handleWishlistToggle(item)}
                                  className={`p-1 rounded-full transition-colors ${
                                    isInWishlist(item.id)
                                      ? 'text-red-500 hover:text-red-600'
                                      : 'text-gray-400 hover:text-red-500'
                                  }`}
                                >
                                  <Heart className={`w-3 h-3 ${isInWishlist(item.id) ? 'fill-current' : ''}`} />
                                </button>
                              )}
                              <button
                                onClick={() => handleRemoveFromCart(item.id)}
                                className="p-1 text-gray-400 hover:text-red-500 rounded-full transition-colors"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Order Summary */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                      <span className="text-gray-900 dark:text-white">{formatPrice(getSubtotal())}</span>
                    </div>
                    {appliedCoupon && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Discount:</span>
                        <span className="text-green-600 dark:text-green-400">-{formatPrice(getDiscount())}</span>
                      </div>
                    )}
                    {/* Payment Option Summary */}
                    {!items.some(item => item.type === 'payment' || item.type === 'split_payment') && (selectedPaymentOption === 'advance' || selectedPaymentOption === 'emi') && (
                      <div className="flex justify-between text-sm border-t border-gray-200 dark:border-gray-700 pt-2">
                        <span className="text-gray-600 dark:text-gray-400">Payment Type:</span>
                        <span className={`font-medium ${
                          selectedPaymentOption === 'advance' 
                            ? 'text-blue-600 dark:text-blue-400' 
                            : 'text-purple-600 dark:text-purple-400'
                        }`}>
                          {selectedPaymentOption === 'advance' ? 'Split Payment' : 'EMI Payment'}
                        </span>
                      </div>
                    )}
                    
                    {/* Payment Item Summary */}
                    {items.some(item => item.type === 'payment' || item.type === 'split_payment') && (
                      <div className="flex justify-between text-sm border-t border-gray-200 dark:border-gray-700 pt-2">
                        <span className="text-gray-600 dark:text-gray-400">Payment Type:</span>
                        <span className="font-medium text-blue-600 dark:text-blue-400">
                          {items.some(item => item.type === 'split_payment') ? 'Split Payment' : 'Installment Payment'}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex justify-between text-lg font-bold border-t border-gray-200 dark:border-gray-700 pt-2">
                      <span className="text-gray-900 dark:text-white">
                        {items.some(item => item.type === 'payment' || item.type === 'split_payment') ? 'Payment Amount:' :
                         selectedPaymentOption === 'advance' ? 'Amount to Pay Now:' : 
                         selectedPaymentOption === 'emi' ? 'Advance Amount:' : 'Total:'}
                      </span>
                      <span className="text-gray-900 dark:text-white">
                        {items.some(item => item.type === 'payment' || item.type === 'split_payment') 
                          ? formatPrice(getTotal())
                          : selectedPaymentOption === 'advance' 
                          ? formatPrice((getTotal() * (paymentOptionsConfig?.advance_payment_percentage || 50)) / 100)
                          : selectedPaymentOption === 'emi'
                          ? formatPrice((getTotal() * (paymentOptionsConfig?.emi_advance_percentage || 50)) / 100)
                          : formatPrice(getTotal())
                        }
                      </span>
                    </div>
                    {!items.some(item => item.type === 'payment' || item.type === 'split_payment') && selectedPaymentOption === 'advance' && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
                        Remaining {formatPrice((getTotal() * (100 - (paymentOptionsConfig?.advance_payment_percentage || 50))) / 100)} will be charged after demo and project completion
                      </div>
                    )}
                    {!items.some(item => item.type === 'payment' || item.type === 'split_payment') && selectedPaymentOption === 'emi' && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
                        Pay {paymentOptionsConfig?.emi_advance_percentage || 50}% advance now, remaining amount in EMI for {paymentOptionsConfig?.emi_tenure_months?.split(',')[0] || '3'} months at {paymentOptionsConfig?.emi_interest_rate || 15}% p.a.
                      </div>
                    )}
                    {items.some(item => item.type === 'payment' || item.type === 'split_payment') && (
                      <div className="text-xs text-blue-600 dark:text-blue-400 text-center mt-2">
                        {items.some(item => item.type === 'split_payment') 
                          ? 'This is a split payment for an existing order' 
                          : 'This is an installment payment for an existing EMI or Split Payment'
                        }
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-4 mt-6">
                    <button
                      onClick={handleBackToCart}
                      className="flex-1 px-6 py-4 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 font-semibold hover:scale-105 flex items-center justify-center space-x-2"
                    >
                      <ArrowLeft className="w-5 h-5" />
                      <span>Back</span>
                    </button>
                    <button
                      onClick={handleProceedToPayment}
                      className="flex-1 px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:scale-105 text-white font-bold rounded-2xl transition-all duration-300 flex items-center justify-center space-x-2 shadow-glow"
                    >
                      <span>
                        {items.some(item => item.type === 'payment') ? 'Pay Installment' :
                         selectedPaymentOption === 'advance' ? 'Pay Advance Amount' : 
                         selectedPaymentOption === 'emi' ? 'Pay Advance & Setup EMI' : 'Proceed to Payment'}
                      </span>
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout; 